/* eslint-disable array-callback-return */
import { createContext, useContext, ReactNode, useState } from 'react';
import { api } from '../../services/services/api';

interface DashboardContextProviderProps {
  children: ReactNode;
}

interface DashboardContextProps {
  getDashboardData(
    dataInicio: string,
    dataFim: string,
    credor?: string | '',
    appAcesso?: number,
  ): Promise<void>;
  setHistorico(newText: string, color: 'brown' | 'green'): void;
  rankingEstados(
    dataInicio: string,
    dataFim: string,
    credor?: string,
    appAcesso?: number,
  ): Promise<void>;
  getSessao(appAcesso?: number): Promise<void>;
  dashboard: Dashboard | undefined;
  text: string;
  color: 'brown' | 'green';
  ranking: Raking;
  sessao: number;
}

interface UFRegiao {
  valor: number;
  uf: string;
  regiao: string;
  quantidade: number;
  qtdAcordos: number;
  acordoAv: string;
  acordoEntrada: string;
  acordoParcela: number;
  data: string;
  credor: number;
}

interface Percentual {
  identificacaoAcesso: string;
  negociacaoAcesso: string;
  seloAcesso: string;
  acordoAcesso: string;
  fundamentacaoAcesso: string;
  fundIdentificacao: string;
  negFund: string;
  seloNeg: string;
  acordoSelo: string;
}

interface Regiao {
  regiao: string;
  valor: number;
  quantidade: number;
}

interface Estado {
  uf: string;
  nome: string;
  quantidade: number;
}

export interface RankingRegiao {
  regiao: string;
  total: number;
  estados: Estado[];
}

interface Raking {
  regiao: RankingRegiao[];
  credores: number[];
  totalAcordos: number;
}

export interface Dashboard {
  totalAcessos: number;
  totalAcessosUnique: number;
  percentual: Percentual;
  totalNegociacao: number;
  totalSelo: number;
  totalAcordos: number;
  totalIdentificacao: number;
  totalFundamentos: number;
  credores: number[];
  ufRegiao: UFRegiao[];
  regiao: Regiao[];
}

const DashboardContext = createContext<DashboardContextProps>(
  {} as DashboardContextProps,
);

export const DashboardContextProvider = ({
  children,
}: DashboardContextProviderProps) => {
  const [dashboard, setDashboard] = useState<Dashboard | undefined>(
    {} as Dashboard,
  );
  const [text, setText] = useState('Real Time');
  const [color, setColor] = useState<'brown' | 'green'>('green');
  const [sessao, setSessao] = useState(0);

  const [ranking, setRaking] = useState<Raking>({} as Raking);

  async function getDashboardData(
    dataInicio: string,
    dataFim: string,
    credor?: string,
    appAcesso?: number,
  ) {
    await api
      .get(`/dashboard`, {
        params: {
          dataInicio,
          dataFim,
          credor: credor === 'Todos' ? undefined : credor,
          appAcesso: appAcesso ? appAcesso : undefined,
        },
      })
      .then(({ data }) => {
        setDashboard(data.dashboard);
      });
  }

  function setHistorico(newText: string, color: 'brown' | 'green') {
    setText(newText);
    setColor(color);
  }

  async function getSessao(appAcesso?: number) {
    await api
      .get(`/dashboard/sessao`, {
        params: {
          appAcesso: appAcesso ? appAcesso : '',
        },
      })
      .then(({ data }) => {
        setSessao(data.sessao);
      });
  }

  function changePosition(arr: any[], from: number, to: number) {
    arr.splice(to, 0, arr.splice(from, 1)[0]);
    return arr;
  }

  async function rankingEstados(
    dataInicio: string,
    dataFim: string,
    credor?: string,
    appAcesso?: number,
  ) {
    await api
      .get(`/dashboard/estados`, {
        params: {
          dataInicio,
          dataFim,
          credor: credor === 'Todos' ? undefined : credor,
          appAcesso: appAcesso ? appAcesso : undefined,
        },
      })
      .then(({ data }) => {
        data.ranking.regiao.map((reg: RankingRegiao, index: number) => {
          if (reg.regiao === 'Outros') {
            changePosition(
              data.ranking?.regiao,
              index,
              data.ranking?.regiao.length - 1,
            );
          }
        });

        setRaking(data.ranking);
      });
  }

  return (
    <DashboardContext.Provider
      value={{
        getDashboardData,
        dashboard,
        text,
        setHistorico,
        color,
        rankingEstados,
        ranking,
        sessao,
        getSessao,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => useContext(DashboardContext);
